/**
 * FHS Real-Time Pricing Manager
 *
 * Handles fetching and updating activity prices from FareHarbor API.
 * Features:
 * - Request batching: Multiple blocks requesting prices are batched into one API call
 * - Deduplication: Same activity requested by multiple blocks only fetches once
 * - Generic DOM updates: Uses data attributes for flexible block support
 */

// Create global namespace if it doesn't exist
window.FHS = window.FHS || {};

/**
 * Format price from cents to display string
 *
 * @param {number|string} priceInCents - Price in cents
 * @param {string} currencyCode - Currency code (e.g., 'USD')
 * @returns {string} Formatted price string
 */
function formatPrice(priceInCents, currencyCode = 'USD') {
  if (priceInCents === null || priceInCents === undefined) return '';

  // Handle "free" prices
  if (
    typeof priceInCents === 'string' &&
    priceInCents.toLowerCase() === 'free'
  ) {
    return 'Free';
  }

  const cents = parseInt(priceInCents, 10);
  if (isNaN(cents)) return '';

  const dollars = cents / 100;

  try {
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode.toUpperCase(),
      minimumFractionDigits: dollars % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(dollars);

    // Ensure there's a space between symbol and number
    return formatted.replace(/^([^\d]+)(\d)/, '$1 $2');
  } catch (e) {
    // Fallback for unsupported currencies
    const decimals = dollars % 1 === 0 ? 0 : 2;
    return `$ ${dollars.toFixed(decimals)}`;
  }
}

/**
 * Escape HTML entities to prevent XSS
 *
 * @param {string} text - Text to escape
 * @returns {string} Escaped text
 */
function escapeHtml(text) {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Update all pricing elements for an activity using data attributes
 *
 * Blocks use these data attributes:
 * - data-activity-id="123" (on wrapper)
 * - data-price-field="low_price" (for from/low price)
 * - data-price-field="customer_type_price" + data-customer-type-pk="456" (for customer type prices)
 *
 * @param {string|number} activityId - The activity ID
 * @param {Object} pricing - Pricing data from API
 */
function updateActivityPricing(activityId, pricing) {
  const activityBlocks = document.querySelectorAll(
    `[data-activity-id="${activityId}"]`,
  );

  activityBlocks.forEach(block => {
    // Check if server already rendered cached pricing
    const isCachedRender = block.dataset.cachedRender === 'true';

    // Skip if already rendered with cached pricing
    if (isCachedRender) {
      return;
    }

    // Update low_price fields
    block.querySelectorAll('[data-price-field="low_price"]').forEach(el => {
      const currentPrice = el.textContent.trim();
      const newPrice = formatPrice(pricing.low_price, pricing.currency);

      // Only update if the price is different
      if (currentPrice !== newPrice) {
        el.textContent = newPrice;
        el.classList.add('fhs-price-updated');
      }
    });

    // Update high_price fields (for range display)
    block.querySelectorAll('[data-price-field="high_price"]').forEach(el => {
      const currentPrice = el.textContent.trim();
      const newPrice = formatPrice(pricing.high_price, pricing.currency);

      // Only update if the price is different
      if (currentPrice !== newPrice) {
        el.textContent = newPrice;
        el.classList.add('fhs-price-updated');
      }
    });

    // Replace entire customer types table with real-time data
    if (pricing.customer_types) {
      const customerTypeKeys = Object.keys(pricing.customer_types);

      // Find the pricing table tbody
      const tbody = block.querySelector('.activity-pricing-block__table tbody');

      if (tbody) {
        // Build new rows from API data (match PHP render.php structure exactly)
        const newRows = customerTypeKeys
          .map(pk => {
            const type = pricing.customer_types[pk];
            const typeLabel = type.label || 'N/A';
            const typeNote = type.note || '';
            const formattedPrice = formatPrice(type.price, pricing.currency);

            // Match PHP: only include details div if note exists AND is not empty/whitespace
            const noteHtml =
              typeNote && typeNote.trim() !== ''
                ? `
                        <div class="activity-pricing-block__details">
                            ${escapeHtml(typeNote)}
                        </div>`
                : '';

            return `
                <tr class="activity-pricing-block__row">
                    <td class="activity-pricing-block__cell activity-pricing-block__cell--type">
                        <div class="activity-pricing-block__label">
                            ${escapeHtml(typeLabel)}
                        </div>${noteHtml}
                    </td>
                    <td class="activity-pricing-block__cell activity-pricing-block__cell--price"
                        data-price-field="customer_type_price"
                        data-customer-type-pk="${escapeHtml(pk)}">
                        ${formattedPrice}
                    </td>
                </tr>`;
          })
          .join('\n');

        // Only update DOM if content actually changed (prevents unnecessary reflows)
        // Normalize whitespace for comparison to handle formatting differences
        const currentHTML = tbody.innerHTML
          .trim()
          .replace(/\s+/g, ' ')
          .replace(/>\s+</g, '><');
        const newHTML = newRows
          .trim()
          .replace(/\s+/g, ' ')
          .replace(/>\s+</g, '><');

        if (currentHTML !== newHTML) {
          tbody.innerHTML = newRows;
        }
      }
    }
  });
}

/**
 * Pricing Request Manager
 * Handles batching and deduplication of pricing requests.
 */
class PricingRequestManager {
  constructor() {
    this.pendingRequests = new Map(); // activityId -> { data, callbacks }
    this.debounceTimer = null;
    this.batchDelay = 100; // ms to wait for more requests
    this.maxBatchSize = 25; // Maximum activities per API request
  }

  /**
   * Request pricing for an activity. Returns a promise that resolves with pricing data.
   * Multiple requests for the same activity are batched and deduplicated.
   *
   * @param {string|number} activityId - The activity ID
   * @param {Object} activityData - Activity data (item_id, shortname, sheet, language)
   * @returns {Promise<Object>} Pricing data
   */
  requestPricing(activityId, activityData) {
    return new Promise((resolve, reject) => {
      // Add to pending requests
      if (!this.pendingRequests.has(activityId)) {
        this.pendingRequests.set(activityId, {
          data: activityData,
          callbacks: [],
        });
      }

      this.pendingRequests.get(activityId).callbacks.push({ resolve, reject });

      // Debounce batch execution
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(
        () => this.executeBatch(),
        this.batchDelay,
      );
    });
  }

  /**
   * Execute the batched pricing request.
   * Automatically splits large batches into multiple requests.
   */
  async executeBatch() {
    const requests = Array.from(this.pendingRequests.entries());
    if (requests.length === 0) return;

    // Clear pending
    const batch = new Map(this.pendingRequests);
    this.pendingRequests.clear();

    // Split into chunks if batch is too large
    const chunks = [];
    for (let i = 0; i < requests.length; i += this.maxBatchSize) {
      chunks.push(requests.slice(i, i + this.maxBatchSize));
    }

    try {
      // Process all chunks in parallel
      const chunkPromises = chunks.map(async chunk => {
        const activities = chunk.map(([id, { data }]) => ({
          activity_id: id,
          ...data,
        }));

        const response = await fetch('/wp-json/fhs/v1/batch-pricing', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ activities }),
        });

        if (!response.ok) {
          throw new Error(`API returned ${response.status}`);
        }

        return await response.json();
      });

      // Wait for all chunks to complete
      const allResults = await Promise.all(chunkPromises);

      // Merge results from all chunks
      const results = Object.assign({}, ...allResults);

      // Update DOM and resolve callbacks for each activity
      for (const [activityId, { callbacks }] of batch.entries()) {
        const pricing = results[activityId];

        if (pricing) {
          // Update DOM using data attributes
          updateActivityPricing(activityId, pricing);
        }

        // Resolve all callbacks for this activity
        callbacks.forEach(cb => cb.resolve(pricing));
      }
    } catch (error) {
      // Reject all callbacks
      for (const [, { callbacks }] of batch.entries()) {
        callbacks.forEach(cb => cb.reject(error));
      }
    }
  }
}

// Create global singleton instance
window.FHS.pricingManager = new PricingRequestManager();

/**
 * Auto-initialize on DOM ready.
 * Finds all pricing blocks and requests updates if real-time pricing is enabled.
 */
function initRealtimePricing() {
  // Check if real-time pricing is enabled
  if (!window.FHS?.realtimePricingEnabled) {
    return;
  }

  // Find all pricing blocks that opt-in to real-time pricing
  const pricingBlocks = document.querySelectorAll(
    '[data-activity-id][data-realtime-pricing="true"]',
  );

  if (pricingBlocks.length === 0) {
    return;
  }

  pricingBlocks.forEach(block => {
    const activityId = block.dataset.activityId;
    const activityData = {
      item_id: block.dataset.itemId,
      shortname: block.dataset.shortname,
      sheet: block.dataset.sheet || '',
      language: block.dataset.language || '',
    };

    // Skip if missing required data
    if (!activityId || !activityData.item_id || !activityData.shortname) {
      return;
    }

    // Request pricing (auto-batched and deduplicated)
    window.FHS.pricingManager.requestPricing(activityId, activityData);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initRealtimePricing);
} else {
  initRealtimePricing();
}
